export function formatTime(ts) {
  return ts
    ? new Date(ts).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
}

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function getDomainName(hostname) {
  const parts = hostname.split(".");
  const filtered = parts.filter(p => !["www", "mail", "m"].includes(p.toLowerCase()));
  return filtered[0] || parts[0];
}

async function getFinalUrl(url) {
  try {
    const response = await fetch(url, { redirect: "follow" });
    return response.url;
  } catch {
    return url;
  }
}

export async function getWebsiteMeta(url) {
  try {
    const response = await fetch(url);
    const html = await response.text();
    let titleMatch = html.match(/<title>(.*?)<\/title>/i);
    let title = titleMatch ? titleMatch[1].trim() : null;
    if (!title) {
      const hostname = new URL(url).hostname;
      const domainName = getDomainName(hostname);
      title = capitalize(domainName);
    }
    let favicon = null;
    const linkIconRegex = /<link[^>]+rel=["'](?:shortcut icon|icon|apple-touch-icon)["'][^>]*>/gi;
    const iconTag = html.match(linkIconRegex);
    if (iconTag && iconTag[0]) {
      const hrefMatch = iconTag[0].match(/href=["']([^"']+)["']/i);
      if (hrefMatch) {
        favicon = hrefMatch[1];
        if (favicon.startsWith("/")) {
          favicon = new URL(favicon, url).href;
        }
        favicon = await getFinalUrl(favicon);
      }
    }
    if (!favicon) {
      favicon = await getFinalUrl(new URL("/favicon.ico", url).href);
    }
    return { title, favicon };
  } catch (err) {
    console.error("Error fetching metadata:", err);
    const hostname = new URL(url).hostname;
    const domainName = getDomainName(hostname);
    return {
      title: capitalize(domainName),
      favicon: await getFinalUrl(new URL("/favicon.ico", url).href),
    };
  }
}

export const fetchIFSCDetails = async (ifscCode) => {
  try {
    const url = `https://ifsc.razorpay.com/${ifscCode}`;
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data;
  } catch (err) {
    return null;
  }
};

export function maskNumber(number) {
  const str = number.toString();
  const last4 = str.slice(-4);
  return "XXXX XXXX XXXX " + last4;
}

export function maskBankAccount(accountNumber) {
  const str = accountNumber.toString();
  const last4 = str.slice(-4);
  const maskedLength = str.length - 4;
  const masked = "X".repeat(maskedLength);
  const grouped = masked.match(/.{1,4}/g)?.join(" ") || "";
  return grouped + " " + last4;
}

export function getPasswordFromItem(item) {
  const field = item?.itemInfo?.find(f => f.name === "PASSWORD");
  return typeof field?.value === "string" ? field.value : "";
}

export function getKeyFromItem(item, key) {
  const field = item?.itemInfo?.find(f => f.name === key);
  return field?.value
}

export function evaluatePassword(password) {
  const pwd = typeof password === "string" ? password : "";

  const issues = [];
  const recommendations = [];

  if (!pwd) {
    return {
      score: 0,
      strength: "Weak",
      riskLevel: "High",
      issues: ["Missing password"],
      recommendations: ["Set a password with at least 12 characters"]
    };
  }

  const commonPasswords = [
    "password",
    "123456",
    "12345678",
    "admin",
    "qwerty"
  ];

  const hasUpper = /[A-Z]/.test(pwd);
  const hasLower = /[a-z]/.test(pwd);
  const hasNumber = /[0-9]/.test(pwd);
  const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
  const repeatRegex = /(.)\1{2,}/;
  const sequenceRegex = /(012|123|234|345|456|567|678|789|abc|bcd|cde)/i;

  // ---------- COMMON PASSWORD (HARD FAIL, BUT EDUCATIONAL) ----------
  const isCommon = commonPasswords.includes(pwd.toLowerCase());
  if (isCommon) {
    issues.push("Common password");
    recommendations.push(
      "Avoid commonly used passwords",
      "Use a unique passphrase that you have never used before"
    );
  }

  // ---------- CHECKS FOR GUIDANCE ----------
  if (!hasUpper) {
    issues.push("No uppercase letter");
    recommendations.push("Add at least one uppercase letter (A–Z)");
  }

  if (!hasLower) {
    issues.push("No lowercase letter");
    recommendations.push("Add at least one lowercase letter (a–z)");
  }

  if (!hasNumber) {
    issues.push("No number");
    recommendations.push("Add at least one number (0–9)");
  }

  if (!hasSpecial) {
    issues.push("No special character");
    recommendations.push("Add at least one special character (!@#$)");
  }

  if (repeatRegex.test(pwd)) {
    issues.push("Repeated characters");
    recommendations.push("Avoid repeating the same character multiple times");
  }

  if (sequenceRegex.test(pwd)) {
    issues.push("Sequential pattern");
    recommendations.push("Avoid sequential patterns like 123 or abc");
  }

  if (pwd.length < 12) {
    issues.push("Password too short");
    recommendations.push("Use at least 12 characters for better security");
  }

  // ---------- SCORE ----------
  let score = 0;

  if (!isCommon) {
    if (pwd.length >= 8) score += 10;
    if (pwd.length >= 12) score += 10;
    if (pwd.length >= 16) score += 5;

    if (hasUpper) score += 8;
    if (hasLower) score += 8;
    if (hasNumber) score += 7;
    if (hasSpecial) score += 7;

    const uniqueChars = new Set(pwd).size;
    score += Math.min(15, uniqueChars);

    if (repeatRegex.test(pwd)) score -= 15;
    if (sequenceRegex.test(pwd)) score -= 15;
  }

  score = Math.max(0, Math.min(100, score));

  const strength =
    score >= 80 ? "Strong" :
      score >= 50 ? "Medium" :
        "Weak";

  return {
    score,
    strength,
    riskLevel:
      strength === "Strong" ? "Low" :
        strength === "Medium" ? "Medium" :
          "High",
    issues: [...new Set(issues)],
    recommendations: [...new Set(recommendations)]
  };
}

export function calculateReuseMap(items = []) {
  const map = {};

  items.forEach(item => {
    const pwd = getPasswordFromItem(item);
    if (!pwd) return;

    map[pwd] = (map[pwd] || 0) + 1;
  });

  return map;
}

export function formatDuration(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);

    let diffMs = Math.abs(end - start);

    const seconds = Math.floor(diffMs / 1000);

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    let result = [];

    if (days) result.push(`${days}d`);
    if (hours) result.push(`${hours}h`);
    if (minutes) result.push(`${minutes}m`);
    if (secs || result.length === 0) result.push(`${secs}s`);

    return result.join(" ");
  }

  export function limitText(text, limit = 30) {
    return text.length > limit
      ? text.slice(0, limit) + "..."
      : text;
  }